'use client'

import React, { useState, useEffect, useRef } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { SplineGraphApiResponse, MalwareSeries, TimeDataPoint } from './MalwareTrends.interface'
import { getGraphData } from './utils'

// Custom hook to load Highcharts modules (only 'highcharts-more' is needed for spline symbols/styles)
const useHighchartsModules = () => {
  const modulesLoaded = useRef(false)

  useEffect(() => {
    if (modulesLoaded.current) return

    import('highcharts/highcharts-more')

    modulesLoaded.current = true
  }, [])
}

// dash styles to cycle through
const DASH_STYLES: Highcharts.DashStyleValue[] = [
  'Solid',
  'Dash',
  'Dot',
  'ShortDash',
  'ShortDot',
  'LongDash',
  'DashDot',
  'LongDashDot',
]

// marker symbols to cycle through
const MARKER_SYMBOLS: Highcharts.SymbolKeyValue[] = [
  'circle',
  'diamond',
  'square',
  'triangle',
  'triangle-down',
]

/**
 * Utility function to process the raw API data for Highcharts.
 */
const processDataForHighcharts = (
  apiData: SplineGraphApiResponse,
): Highcharts.SeriesSplineOptions[] => {
  if (!apiData || !apiData.data) return []

  return apiData.data.map((series: MalwareSeries, index: number) => {
    const chartData = series.data.map((point: TimeDataPoint) => [
      new Date(point.week_datetime).getTime(),
      point.samples_count,
    ])

    return {
      type: 'spline',
      name: series.name.charAt(0).toUpperCase() + series.name.slice(1),
      data: chartData,
      // Assign a unique dash style and marker to each series
      dashStyle: DASH_STYLES[index % DASH_STYLES.length],
      marker: {
        enabled: true,
        symbol: MARKER_SYMBOLS[index % MARKER_SYMBOLS.length],
      },
    }
  })
}

// single style object for all chart text.
const chartTextStyle: Highcharts.CSSObject = {
  fontFamily: 'var(--gt-haptik-font-family)',
  color: '#FFFFFF',
}

// lighter text (axis labels, etc.)
const lightTextStyle: Highcharts.CSSObject = {
  fontFamily: 'var(--gt-haptik-font-family)',
  color: '#b0b0b0',
}

/**
 * Main component to render the Prevalent Malwares graph.
 */
const GraphPrevalentMalwares: React.FC = () => {
  // Call the hook to initialize Highcharts modules
  useHighchartsModules()

  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Explicitly cast the 'any' response from the fetcher
        const apiData = (await getGraphData('continuous')) as SplineGraphApiResponse

        const highchartsSeries = processDataForHighcharts(apiData)

        setChartOptions({
          chart: {
            type: 'spline',
            style: chartTextStyle,
          },
          title: {
            text: 'Most prevalent malware',
            style: chartTextStyle,
          },
          xAxis: {
            type: 'datetime',
            // e.g. "Jan 5, 2006" (default omits year for sub-year ranges)
            dateTimeLabelFormats: {
              millisecond: '%b %e, %Y',
              second: '%b %e, %Y',
              minute: '%b %e, %Y',
              hour: '%b %e, %Y',
              day: '%b %e, %Y',
              week: '%b %e, %Y',
              month: '%b %Y',
              year: '%Y',
            },
            labels: { style: lightTextStyle },
            title: { text: 'Time', style: lightTextStyle },
            lineColor: '#333d47',
            tickColor: '#333d47',
          },
          yAxis: {
            title: {
              text: 'Percentage usage',
              style: lightTextStyle,
            },
            labels: {
              style: lightTextStyle,
            },
            gridLineColor: '#333d47',
          },
          legend: {
            enabled: true,
            itemStyle: {
              color: '#FFFFFF',
              fontWeight: 'bold',
            },
            itemHoverStyle: {
              color: '#b0b0b0',
            },
          },
          tooltip: {
            shared: true,
            pointFormat:
              '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:.2f}%</b><br/>',
          },
          plotOptions: {
            spline: {
              marker: { enabled: true, radius: 3 },
            },
          },
          series: highchartsSeries,
          credits: { enabled: false },
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">
        Loading graph data. Please wait...
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">Error: {error}</div>
    )
  }
  if (!chartOptions) {
    return (
      <div className="w-full text-center py-[100px] text-white text-[16px]">No data available.</div>
    )
  }
  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}

export default GraphPrevalentMalwares
